import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { UsedAPI, BackendDataShape } from "api/config";
import CookieMonster from "api/CookieMonster";
import { convertToEncodedURL } from "assets/usefulFunctions";
import {
  APaginationEntity,
  BackendDataPageShape,
  BackendPaginationRequestObject,
} from "types";
import {
  AMaterialStatus,
  AnOperation,
  AnOperationParams,
  APersonelStatus,
} from "types/endpoints/operation";

export const operationStoreAPI = createApi({
  reducerPath: "operationStoreAPI",
  baseQuery: fetchBaseQuery({
    baseUrl: `${UsedAPI}/operation`,
    prepareHeaders: (headers) => {
      const token = CookieMonster.loadCookie("jwt");
      headers.set("Authorization", `Bearer ${token}`);
      return headers;
    },
  }),
  tagTypes: ["OperationList", "PersonelStatus", "MaterialStatus"],
  endpoints: (builder) => {
    return {
      getOperation: builder.query<
        APaginationEntity<AnOperation[]>,
        BackendPaginationRequestObject<Partial<AnOperationParams>>
      >({
        query: (obj) => {
          let params = "";
          if (obj) {
            params = convertToEncodedURL(obj);
            params = params.length ? "?" + params : "";
          }
          let url = params;
          return {
            url,
            method: "GET",
          };
        },
        transformResponse: (response: BackendDataPageShape<AnOperation[]>) => {
          const { meta, entities } = response.data;
          const dataWithChip = entities.map((val) => {
            return {
              ...val,
            };
          });
          return { meta, entities: dataWithChip };
        },
        providesTags: ["OperationList"],
      }),
      getOperationById: builder.query<AnOperation, Partial<AnOperation>>({
        query: ({ id, ...rest }) => {
          let params = "";
          if (rest) {
            params = convertToEncodedURL(rest);
            params = params.length ? "?" + params : "";
          }
          let url = `/${id}` + params;
          return {
            url,
            method: "GET",
          };
        },
        transformResponse: (response: BackendDataShape<AnOperation>) =>
          response.data,
        providesTags: ["OperationList"],
      }),
      createOperation: builder.mutation<
        BackendDataShape<AnOperation>,
        Partial<AnOperation>
      >({
        query: (obj) => {
          return {
            url: ``,
            body: obj,
            method: "POST",
          };
        },
        invalidatesTags: ["OperationList"],
      }),
      updateOperation: builder.mutation<
        BackendDataShape<AnOperation>,
        { id: number }
      >({
        query: ({ id, ...rest }) => {
          return {
            url: `/${id}`,
            body: rest,
            method: "PATCH",
          };
        },
        invalidatesTags: ["OperationList"],
      }),
      deleteOperation: builder.mutation<
        BackendDataShape<string>,
        { id: number }
      >({
        query: ({ id }) => {
          return {
            url: `/${id}`,
            method: "DELETE",
          };
        },
        invalidatesTags: ["OperationList"],
      }),
      getMaterialStatus: builder.query<
        AMaterialStatus[],
        { search: string | undefined }
      >({
        query: (obj) => {
          let params = "";
          if (obj) {
            params = convertToEncodedURL(obj);
            params = params.length ? "?" + params : "";
          }
          let url = "/materiel" + params;
          return {
            url,
            method: "GET",
          };
        },
        transformResponse: (response: BackendDataShape<AMaterialStatus[]>) =>
          response.data,
        providesTags: ["MaterialStatus"],
      }),
      getPersonelStatus: builder.query<
        APersonelStatus[],
        { search: string | undefined }
      >({
        query: (obj) => {
          let params = "";
          if (obj) {
            params = convertToEncodedURL(obj);
            params = params.length ? "?" + params : "";
          }
          let url = "/personel" + params;
          return {
            url,
            method: "GET",
          };
        },
        transformResponse: (response: BackendDataShape<APersonelStatus[]>) =>
          response.data,
        providesTags: ["PersonelStatus"],
      }),
    };
  },
});

export const {
  useGetOperationQuery,
  useGetOperationByIdQuery,
  useCreateOperationMutation,
  useUpdateOperationMutation,
  useDeleteOperationMutation,
  useGetMaterialStatusQuery,
  useGetPersonelStatusQuery,
  util: { resetApiState: resetOperationStoreAPI },
} = operationStoreAPI;
